import { useState, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { WizardStep } from '../types';
import { CreateBookFormData } from '../types';

export function useStepValidation(
  step: WizardStep, 
  form: UseFormReturn<CreateBookFormData>,
  storyType?: 'single' | 'multi',
  selectedTemplate?: any
): boolean {
  const [isValid, setIsValid] = useState(false);
  
  // Watch specific fields based on current step to trigger re-validation
  let watchedValues: any = {};
  
  switch (step) {
    case 'story-type':
      watchedValues.storyType = form.watch('storyType');
      break;
      
    case 'photo-upload':
      watchedValues.characterPhoto = form.watch('characterPhoto');
      if (storyType === 'single') {
        // For single character photo-only step, we don't need name/age here
      } else if (storyType === 'multi') {
        watchedValues.detectedFaces = form.watch('detectedFaces');
        watchedValues.characterCount = form.watch('characterCount');
        watchedValues.characters = form.watch('characters');
      }
      break;
      
    case 'character-details':
      watchedValues.childFirstName = form.watch('childFirstName');
      watchedValues.childAge = form.watch('childAge');
      watchedValues.childGender = form.watch('childGender');
      break;
      
    case 'character-details-review':
      watchedValues.childFirstName = form.watch('childFirstName');
      watchedValues.childAge = form.watch('childAge');
      watchedValues.childGender = form.watch('childGender');
      break;
      
    case 'multi-character-details':
      watchedValues.characters = form.watch('characters');
      break;
      
    case 'appearance':
      watchedValues.hairColor = form.watch('hairColor');
      watchedValues.skinColor = form.watch('skinColor');
      break;
      
    case 'multi-character-appearance':
      watchedValues.characters = form.watch('characters');
      break;
      
    case 'style':
      watchedValues.styleId = form.watch('styleId');
      break;
      
    case 'story-creation':
      watchedValues.bookTitle = form.watch('bookTitle');
      watchedValues.additionalComments = form.watch('additionalComments');
      break;
      
    // No watches needed for these steps
    case 'appearance-review':
    case 'story-selection':
    case 'multi-story-selection':
    case 'story-preview':
      break;
  }
  
  // Create a stable stringified version of watchedValues for dependency tracking
  // This prevents the "changed size between renders" error
  const watchedValuesKey = JSON.stringify(watchedValues);
  
  useEffect(() => {
    const validateStep = () => {
      let valid = false;
      
      switch (step) {
        case 'story-type':
          valid = !!form.getValues('storyType');
          break;
          
        case 'photo-upload': {
          const photoUrl = form.getValues('characterPhoto');
          
          // Photo is always required - empty string should fail validation
          if (!photoUrl || photoUrl === '') {
            valid = false;
            break;
          }
          
          if (storyType === 'single') {
            // For single character, photo-upload step only needs the photo
            valid = true;
          } else if (storyType === 'multi') {
            // FOR MULTI-CHARACTER: Check if faces are detected and have names
            const detectedFaces = form.getValues('detectedFaces') || [];
            const selectedFaces = detectedFaces.filter((face: any) => face.selected);
            const allHaveNames = selectedFaces.length > 0 && 
              selectedFaces.every((face: any) => face.name && face.name.trim() !== '');
            
            valid = allHaveNames;
          } else {
            valid = true;
          }
          break;
        }
          
        case 'character-details-review': {
          const childName = form.getValues('childFirstName');
          const childAge = form.getValues('childAge');
          const childGender = form.getValues('childGender');
          // Trim the name for validation
          valid = !!(childName?.trim() && childAge && childGender);
          break;
        }
          
        case 'appearance-review':
          valid = true; // Always valid, just review
          break;
          
        case 'story-selection':
        case 'multi-story-selection':
          valid = !!selectedTemplate;
          break;
          
        case 'character-details': {
          const childName = form.getValues('childFirstName');
          const childAge = form.getValues('childAge');
          const childGender = form.getValues('childGender');
          // Trim the name for validation
          valid = !!(childName?.trim() && childAge && childGender);
          break;
        }
          
        case 'multi-character-details': {
          const characters = form.getValues('characters') || [];
          valid = characters.length > 0 && 
            characters.every(char => char.name && char.age && char.gender);
          break;
        }
          
        case 'appearance': {
          const hairColor = form.getValues('hairColor');
          const skinColor = form.getValues('skinColor');
          valid = !!(hairColor && skinColor);
          break;
        }
          
        case 'multi-character-appearance': {
          const chars = form.getValues('characters') || [];
          valid = chars.length > 0 && 
            chars.every(char => char.skinTone && char.hairColor && char.eyeColor && char.hairTexture);
          break;
        }
          
        case 'style':
          valid = !!form.getValues('styleId');
          break;
          
        case 'story-creation': {
          const bookTitle = form.getValues('bookTitle');
          const additionalComments = form.getValues('additionalComments');
          valid = !!(bookTitle && additionalComments && bookTitle.length >= 3 && additionalComments.length >= 5);
          break;
        }
          
        case 'story-preview':
          valid = true; // No validation needed for these steps
          break;
          
        default:
          valid = false;
      }
      
      setIsValid(valid);
    };
    
    // Run validation immediately
    validateStep();
    
    // REMOVED: form.watch() subscription that was causing React Error #310
    // The validation will now only run when the hook is re-rendered
    // which happens when form values change due to React Hook Form's internal mechanics
    
  }, [step, form, storyType, selectedTemplate, watchedValuesKey]); // Use stable stringified key instead of spreading values
  
  return isValid;
}
